import { purple } from '@ant-design/colors'
import {
  CrownOutlined,
  EditOutlined,
  GlobalOutlined,
  LinkOutlined,
  MailOutlined,
  MessageOutlined,
  PhoneOutlined,
  WhatsAppOutlined,
} from '@ant-design/icons'
import { SevenroomsIcon } from '@components/third-party/icons/Icons'
import { Button, Card, Flex, Space, Typography } from 'antd'
import { FC, ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useTheme } from '../../contexts/ThemeContext'

const { Text } = Typography

export type AvailableAppConnector =
  | 'sevenrooms'
  | 'bookingLink'
  | 'phone'
  | 'whatsapp'
  | 'intercom'
  | 'membershipChat'
  | 'email'

/**
 * Display name of each connector, hoisted out of the component so copy written
 * elsewhere (e.g. the venue Overview empty states) names a connector exactly
 * the way the connectors screen does.
 */
export const appConnectorNames: Record<AvailableAppConnector, string> = {
  sevenrooms: 'Sevenrooms',
  bookingLink: 'External link',
  phone: 'Phone',
  whatsapp: 'WhatsApp',
  intercom: 'Intercom',
  membershipChat: 'Membership chat',
  email: 'Email',
}

interface Props {
  type: AvailableAppConnector
  onClick: () => void
  disabled?: boolean
  connected?: boolean
}

const Summary: FC<Props> = ({
  type,
  onClick,
  disabled = false,
  connected = false,
}) => {
  const footer = undefined

  const { mode } = useTheme()

  const typesConfiguration: Record<
    AvailableAppConnector,
    {
      name: string
      description: string
      icon: ReactNode
      background: string
      border: string
      color: string
    }
  > = {
    sevenrooms: {
      name: appConnectorNames.sevenrooms,
      description:
        'Connect your Sevenrooms account to enable your users to book by the app.',
      icon: (
        <SevenroomsIcon
          width={22}
          height={22}
          color={mode === 'dark' ? '#fff' : '#000'}
        />
      ),
      background: mode === 'dark' ? '#141414' : '#f0f0f0',
      border: mode === 'dark' ? '#ddd' : '#000',
      color: '',
    },
    bookingLink: {
      name: appConnectorNames.bookingLink,
      description:
        'Add an external booking link directly accessible from the in-app page. ',
      icon: <GlobalOutlined style={{ fontSize: 24, color: '#F759AB' }} />,
      background: mode === 'dark' ? '#141414' : '#FFF0F6',
      border: '#F759AB',
      color: '',
    },
    phone: {
      name: appConnectorNames.phone,
      description:
        'Add a phone number on the in-app page to allow your users to book the venue by phone.',
      icon: <PhoneOutlined style={{ color: purple[3], fontSize: 24 }} />,
      background: mode === 'dark' ? '#141414' : '#f9f0ff',
      border: '#b37feb',
      color: '',
    },
    whatsapp: {
      name: appConnectorNames.whatsapp,
      description:
        'Add a WhatsApp phone number on the in-app page to allow your users to book via WhatsApp.',
      icon: <WhatsAppOutlined style={{ color: '#25D366', fontSize: 24 }} />,
      background: mode === 'dark' ? '#141414' : '#F6FFED',
      border: '#52C41A',
      color: '',
    },
    intercom: {
      name: appConnectorNames.intercom,
      description:
        'Allow your users to book the venue by Intercom chat and customize the placeholder message.',
      icon: <MessageOutlined style={{ fontSize: 24 }} />,
      background: mode === 'dark' ? '#141414' : '#e6f7ff',
      border: '#4096FF',
      color: '',
    },
    membershipChat: {
      name: appConnectorNames.membershipChat,
      description:
        'This connector is dedicated to members only. Pressing the “Book” button will open the membership chat. For non-members, the “Book” button will not be displayed.',
      icon: <CrownOutlined style={{ color: '#13C2C2', fontSize: 24 }} />,
      background: mode === 'dark' ? '#141414' : '#E6FFFB',
      border: '#13C2C2',
      color: '',
    },
    email: {
      name: appConnectorNames.email,
      description:
        'Add an email address on the in-app page to allow your users to book the venue directly via email.',
      icon: <MailOutlined style={{ color: '#FFD666', fontSize: 24 }} />,
      background: mode === 'dark' ? '#141414' : '#FFF7E6',
      border: '#FFD666',
      color: '',
    },
  }

  return (
    <Card
      style={{
        backgroundColor: typesConfiguration[type].background,
        borderColor: typesConfiguration[type].border,
        opacity: disabled ? 0.4 : 1,
        pointerEvents: disabled ? 'none' : undefined,
        userSelect: disabled ? 'none' : undefined,
      }}
      styles={{ body: { padding: 16 } }}
    >
      <Space orientation="vertical">
        <Flex className="w-full align-items-center" gap={8}>
          {typesConfiguration[type].icon}
          <Text style={{ fontWeight: 'bold' }}>
            {typesConfiguration[type].name}
          </Text>
        </Flex>
        {typesConfiguration[type].description &&
          typesConfiguration[type].description}
        {footer && footer}
        <Flex gap={8}>
          <Button
            onClick={onClick}
            style={{
              borderColor: typesConfiguration[type].border,
              color: typesConfiguration[type].color,
            }}
            variant={'outlined'}
            icon={connected ? <EditOutlined /> : <LinkOutlined />}
            block
          >
            {connected ? 'Edit configuration' : 'Connect'}
          </Button>
          {connected && type == 'sevenrooms' && (
            <Link target="blank" to={'/settings/booking/availability-viewer'}>
              <Button
                style={{
                  borderColor: typesConfiguration[type].border,
                  color: typesConfiguration[type].color,
                }}
                variant={'outlined'}
              >
                Test configuration
              </Button>
            </Link>
          )}
        </Flex>
      </Space>
    </Card>
  )
}

type SummaryType = { Summary: typeof Summary }

export { Summary, type SummaryType }
